<template>
  <div class="tab-content add-on-page">
    <div class="main-header add-on-header">
      <div>
        <h1>{{ $t('controlCenter.sidebar.addOnManagement') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.addOnManagement.subtitle') }}</p>
      </div>
      <div v-if="!checkoutVisible" class="header-actions">
        <el-button
          icon="el-icon-shopping-cart-2"
          @click="goToShop"
        >
          {{ $t('controlCenter.common.shop') }}
        </el-button>
        <el-button
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadOptions"
        >
          {{ $t('controlCenter.common.refresh') }}
        </el-button>
      </div>
      <el-button
        v-else
        icon="el-icon-arrow-left"
        @click="returnToBuilder"
      >
        {{ $t('controlCenter.addOnManagement.backToSelection') }}
      </el-button>
    </div>

    <div v-if="checkoutVisible" class="checkout-panel">
      <div class="checkout-heading">
        <span class="checkout-kicker">{{ $t('controlCenter.addOnManagement.checkout') }}</span>
        <h2>{{ $t('controlCenter.addOnManagement.completePayment') }}</h2>
        <p>{{ $t('controlCenter.addOnManagement.paymentIntro') }}</p>
      </div>
      <PaymentStep
        :key="createdOrder && createdOrder.orderNo"
        :final-amount="checkoutAmount"
        :order-data="paymentOrderData"
        :order-no="createdOrder && createdOrder.orderNo"
        :purchase-config="paymentConfig"
        :allow-subscription="false"
        @payment-success="handlePaymentSuccess"
      />
    </div>

    <template v-else>
      <section v-if="!loading && !loadError" class="package-status-panel" :class="{ blocked: isStarterOnlyPackage }">
        <div>
          <span class="section-eyebrow">{{ $t('controlCenter.addOnManagement.currentPackage') }}</span>
          <h2>{{ currentPackageText }}</h2>
          <p>{{ packageRestrictionText }}</p>
        </div>
        <span class="package-badge" :class="{ starter: isStarterOnlyPackage }">
          {{ packageBadgeText }}
        </span>
      </section>

      <div v-if="loading" class="state-card add-on-state">
        <i class="fas fa-circle-notch fa-spin"></i>
        <p>{{ $t('controlCenter.common.loading') }}</p>
      </div>

      <div v-else-if="loadError" class="state-card add-on-state">
        <i class="fas fa-triangle-exclamation"></i>
        <h3>{{ $t('controlCenter.addOnManagement.loadErrorTitle') }}</h3>
        <p>{{ $t('controlCenter.addOnManagement.loadError') }}</p>
        <el-button type="primary" @click="loadOptions">{{ $t('controlCenter.common.refresh') }}</el-button>
      </div>

      <div v-else class="add-on-workspace">
        <section class="catalog-column">
          <div class="duration-panel">
            <div>
              <span class="section-eyebrow">{{ $t('controlCenter.addOnManagement.purchaseDuration') }}</span>
              <h2>{{ $t('controlCenter.addOnManagement.chooseDuration') }}</h2>
            </div>
            <div class="duration-switch" role="group">
              <button
                v-for="option in durationOptions"
                :key="option.value"
                type="button"
                :class="{ active: billingCycle === option.value }"
                @click="billingCycle = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            v-for="category in categories"
            :key="category.key"
            class="category-section"
            :class="`category-${category.key}`"
          >
            <div class="category-heading">
              <div>
                <span class="section-eyebrow">{{ category.countLabel }}</span>
                <h2>{{ category.title }}</h2>
              </div>
              <p>{{ category.description }}</p>
            </div>

            <div v-if="category.key === 'licences'" class="licence-step-wrapper">
              <div class="licence-step-card" :class="{ selected: licenceQuantity > 0 }">
                <span class="card-check">
                  <i class="el-icon-check"></i>
                </span>
                <span class="item-icon auditor-icon">
                  <i class="el-icon-user-solid"></i>
                </span>
                <div class="licence-main">
                  <span class="item-type">{{ $t('controlCenter.addOnManagement.types.licence') }}</span>
                  <strong>{{ $t('controlCenter.addOnManagement.licencesName', { count: licenceQuantity }) }}</strong>
                  <span class="item-meta">{{ $t('controlCenter.addOnManagement.licencesDesc') }}</span>
                  <span class="licence-unit-price">
                    {{ $t('controlCenter.addOnManagement.licenceUnitPrice', { price: formatPrice(fromMinor(licenceUnitPriceMinor)) }) }}
                  </span>
                </div>
                <div class="licence-control" @click.stop>
                  <button
                    type="button"
                    class="licence-counter-btn"
                    :disabled="isAddOnBlocked || licenceQuantity <= 0"
                    :aria-label="$t('controlCenter.addOnManagement.decreaseLicence')"
                    @click="decreaseLicence"
                  >
                    <i class="el-icon-minus"></i>
                  </button>
                  <div class="licence-count">
                    <span>{{ licenceQuantity }}</span>
                    <small>{{ $t('controlCenter.addOnManagement.licenceCountLabel') }}</small>
                  </div>
                  <button
                    type="button"
                    class="licence-counter-btn"
                    :disabled="isAddOnBlocked || licenceQuantity >= licenceMaxQuantity"
                    :aria-label="$t('controlCenter.addOnManagement.increaseLicence')"
                    @click="increaseLicence"
                  >
                    <i class="el-icon-plus"></i>
                  </button>
                </div>
                <div class="licence-total">
                  <span>{{ formatPrice(licenceDisplayAmount) }}</span>
                  <small>{{ durationSuffix }}</small>
                </div>
              </div>
            </div>

            <div v-else-if="category.items.length" class="item-grid">
              <button
                v-for="item in category.items"
                :key="item.key"
                type="button"
                class="add-on-card"
                :class="{ selected: isSelected(item.key), disabled: isAddOnBlocked }"
                :disabled="isAddOnBlocked"
                @click="toggleItem(item)"
              >
                <span class="card-check">
                  <i class="el-icon-check"></i>
                </span>
                <span class="item-icon" :class="itemIconClass(item)">
                  <i :class="itemIcon(item)"></i>
                </span>
                <span class="item-content">
                  <span class="item-type">{{ item.typeLabel }}</span>
                  <strong>{{ item.name }}</strong>
                  <span class="item-meta">{{ item.description }}</span>
                </span>
                <span class="item-footer">
                  <span>{{ formatPrice(getItemPrice(item)) }}</span>
                  <small>{{ durationSuffix }}</small>
                </span>
                <span v-if="item.expiresAt" class="item-expiry">
                  {{ $t('controlCenter.addOnManagement.currentExpiry', { date: formatDate(item.expiresAt) }) }}
                </span>
              </button>
            </div>

            <div v-else class="empty-category">
              {{ $t('controlCenter.addOnManagement.emptyCategory') }}
            </div>
          </div>
        </section>

        <aside class="summary-column">
          <div class="summary-panel">
            <div class="summary-header">
              <span class="section-eyebrow">{{ $t('controlCenter.addOnManagement.orderSummary') }}</span>
              <h2>{{ $t('controlCenter.addOnManagement.selectedAddOns') }}</h2>
            </div>

            <div v-if="selectedDetails.length" class="summary-list">
              <div
                v-for="item in selectedDetails"
                :key="item.key"
                class="summary-row"
              >
                <div>
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.quantityLabel }}</span>
                  <small>{{ expiryLabel(item) }}</small>
                </div>
                <em>{{ formatPrice(getItemPrice(item)) }}</em>
              </div>
            </div>

            <div v-else class="summary-empty">
              <i class="el-icon-shopping-cart-2"></i>
              <p>{{ $t('controlCenter.addOnManagement.emptySelection') }}</p>
            </div>

            <div class="total-box">
              <span>{{ $t('controlCenter.addOnManagement.total') }}</span>
              <strong>{{ formatPrice(totalAmount) }}</strong>
            </div>

            <el-button
              type="primary"
              class="confirm-button"
              :loading="creatingOrder"
              :disabled="isAddOnBlocked || !selectedDetails.length"
              @click="createOrder"
            >
              {{ $t('controlCenter.addOnManagement.confirmAndPay') }}
            </el-button>
            <p class="summary-note">{{ $t('controlCenter.addOnManagement.summaryNote') }}</p>
          </div>
        </aside>
      </div>

      <div class="mobile-confirm-bar">
        <div>
          <span>{{ $t('controlCenter.addOnManagement.total') }}</span>
          <strong>{{ formatPrice(totalAmount) }}</strong>
        </div>
        <el-button
          type="primary"
          :loading="creatingOrder"
          :disabled="isAddOnBlocked || !selectedDetails.length"
          @click="createOrder"
        >
          {{ $t('controlCenter.addOnManagement.confirmAndPay') }}
        </el-button>
      </div>
    </template>
  </div>
</template>

<script>
import PaymentStep from '@/components/buy/PaymentStep.vue'
import { paymentAdminApi } from '@/api/paymentAdmin'
import { formatPrice as formatPriceValue } from '@/utils/currency'

const FALLBACK_LANGUAGE_CODES = {
  english: 'EN',
  french: 'FR',
  spanish: 'ES',
  chinese: 'ZH',
  german: 'DE',
  polish: 'PL',
  czech: 'CS',
  slovak: 'SK',
  hungarian: 'HU',
  romanian: 'RO',
  bulgarian: 'BG',
  serbian: 'SR',
  croatian: 'HR',
  slovenian: 'SL'
}

export default {
  name: 'AddOnManagement',
  components: {
    PaymentStep
  },
  data() {
    return {
      loading: false,
      loadError: false,
      creatingOrder: false,
      billingCycle: 'one_year',
      purchaseConfig: null,
      addOnOptions: null,
      myProducts: [],
      catalogItems: [],
      selectedKeys: [],
      licenceQuantity: 0,
      createdOrder: null,
      checkoutVisible: false
    }
  },
  computed: {
    durationOptions() {
      return [
        { value: 'one_year', label: this.$t('controlCenter.billingCycleLabels.one_year') },
        { value: 'two_year', label: this.$t('controlCenter.billingCycleLabels.two_year') }
      ]
    },
    durationYears() {
      return this.billingCycle === 'two_year' ? 2 : 1
    },
    durationSuffix() {
      return this.billingCycle === 'two_year'
        ? this.$t('controlCenter.addOnManagement.forTwoYears')
        : this.$t('controlCenter.addOnManagement.forOneYear')
    },
    paymentConfig() {
      return (this.addOnOptions && this.addOnOptions.paymentConfig) || this.purchaseConfig || {}
    },
    currency() {
      return (this.paymentConfig && this.paymentConfig.displayCurrency) || 'RMB'
    },
    activePackages() {
      return this.myProducts
        .filter(product => product && product.kind === 'PLAN' && !this.isExpired(product))
        .sort((a, b) => this.dateValue(b.expiresAt) - this.dateValue(a.expiresAt))
    },
    currentPackageIds() {
      return Array.from(new Set(this.activePackages
        .map(product => product.planId || product.itemCode)
        .filter(Boolean)))
    },
    hasActivePackage() {
      return this.currentPackageIds.length > 0
    },
    isStarterOnlyPackage() {
      return !this.hasActivePackage || this.currentPackageIds.every(planId => String(planId).toLowerCase() === 'starter')
    },
    isAddOnBlocked() {
      return this.isStarterOnlyPackage
    },
    currentPackageText() {
      if (!this.hasActivePackage) {
        return this.$t('controlCenter.addOnManagement.noPackage')
      }
      return this.currentPackageIds.map(this.planLabel).join(' / ')
    },
    packageBadgeText() {
      return this.isStarterOnlyPackage
        ? this.$t('controlCenter.addOnManagement.addOnLocked')
        : this.$t('controlCenter.addOnManagement.addOnAvailable')
    },
    packageRestrictionText() {
      return this.isStarterOnlyPackage
        ? this.$t('controlCenter.addOnManagement.starterRestriction')
        : this.$t('controlCenter.addOnManagement.packageAvailable')
    },
    categories() {
      return [
        {
          key: 'languages',
          title: this.$t('controlCenter.addOnManagement.categories.languages.title'),
          description: this.$t('controlCenter.addOnManagement.categories.languages.desc'),
          countLabel: this.$t('controlCenter.addOnManagement.itemCount', { count: this.languageItems.length }),
          items: this.languageItems
        },
        {
          key: 'standards',
          title: this.$t('controlCenter.addOnManagement.categories.standards.title'),
          description: this.$t('controlCenter.addOnManagement.categories.standards.desc'),
          countLabel: this.$t('controlCenter.addOnManagement.itemCount', { count: this.standardItems.length }),
          items: this.standardItems
        },
        {
          key: 'licences',
          title: this.$t('controlCenter.addOnManagement.categories.licences.title'),
          description: this.$t('controlCenter.addOnManagement.categories.licences.desc'),
          countLabel: this.$t('controlCenter.addOnManagement.licenceSelectedCount', { count: this.licenceQuantity }),
          items: this.licenceItems
        }
      ]
    },
    languageItems() {
      const backendItems = this.normalizeBackendItems(['languages', 'languageOptions'], 'LANGUAGE')
      if (backendItems.length) return backendItems
      const languages = this.purchaseConfig && Array.isArray(this.purchaseConfig.languages) ? this.purchaseConfig.languages : []
      return languages.map(language => {
        const code = language.code || FALLBACK_LANGUAGE_CODES[language.id] || language.id
        const catalog = this.findCatalogItem('LANGUAGE', language.id || language.code)
        return this.createItem({
          itemType: 'LANGUAGE',
          itemCode: language.id || language.code,
          name: this.languageLabel(language),
          description: String(code || '').toUpperCase(),
          quantity: 1,
          priceOneYear: language.priceOneYear || language.price || (catalog && catalog.priceOneYear) || 0,
          priceTwoYears: language.priceTwoYears || (catalog && catalog.priceTwoYears),
          expiresAt: language.expiresAt || language.currentExpiresAt
        })
      })
    },
    standardItems() {
      const backendItems = this.normalizeBackendItems(['standards', 'standardOptions'], 'STANDARD')
      if (backendItems.length) return backendItems
      const standards = this.purchaseConfig && Array.isArray(this.purchaseConfig.standards) ? this.purchaseConfig.standards : []
      return standards.map(standard => {
        const catalog = this.findCatalogItem('STANDARD', standard.id || standard.code)
        return this.createItem({
          itemType: 'STANDARD',
          itemCode: standard.id || standard.code,
          name: standard.name || standard.id || standard.code,
          description: this.$t('controlCenter.addOnManagement.standardTemplate'),
          quantity: 1,
          priceOneYear: standard.priceOneYear || standard.price || (catalog && catalog.priceOneYear) || 0,
          priceTwoYears: standard.priceTwoYears || (catalog && catalog.priceTwoYears),
          expiresAt: standard.expiresAt || standard.currentExpiresAt
        })
      })
    },
    licenceItems() {
      if (this.licenceQuantity <= 0) {
        return []
      }
      return [
        this.createItem({
          itemType: 'AUDITOR_SEAT',
          itemCode: 'auditor-seat',
          name: this.$t('controlCenter.addOnManagement.licencesName', { count: this.licenceQuantity }),
          description: this.$t('controlCenter.addOnManagement.licencesDesc'),
          quantity: this.licenceQuantity,
          priceOneYear: this.licenceOneYearMinor,
          priceTwoYears: this.licenceTwoYearsMinor
        })
      ]
    },
    visibleAuditorSeatOptions() {
      const configOptions = this.purchaseConfig && Array.isArray(this.purchaseConfig.auditorSeatPrices)
        ? this.purchaseConfig.auditorSeatPrices.map(item => ({
          seats: Number(item.seats || 0),
          pricePerSeat: Number(item.pricePerSeat || item.monthlyPricePerSeat || item.pricePerMonth || 0)
        }))
        : []
      const catalogOptions = this.catalogItems
        .filter(item => String(item.itemType || '').toUpperCase() === 'AUDITOR_SEAT')
        .map(item => ({
          seats: Number(item.seatCount || item.seats || 0),
          pricePerSeat: this.normalizeLicenceMonthlyUnitPrice(item)
        }))
      const options = (configOptions.length ? configOptions : catalogOptions)
        .filter(item => item.seats > 0 && item.seats < 50)
        .sort((a, b) => a.seats - b.seats)
      return options.length ? options : [
        { seats: 1, pricePerSeat: 6000 },
        { seats: 3, pricePerSeat: 5800 },
        { seats: 5, pricePerSeat: 5600 },
        { seats: 15, pricePerSeat: 5300 },
        { seats: 25, pricePerSeat: 5100 }
      ]
    },
    licenceMaxQuantity() {
      return 49
    },
    licencePricingTier() {
      const quantity = Math.max(1, Number(this.licenceQuantity || 1))
      return this.visibleAuditorSeatOptions.reduce((selected, option) => {
        if (option.seats <= quantity) {
          return option
        }
        return selected
      }, this.visibleAuditorSeatOptions[0])
    },
    licenceUnitPriceMinor() {
      return this.licencePricingTier ? Number(this.licencePricingTier.pricePerSeat || 0) : 0
    },
    licenceOneYearMinor() {
      return this.licenceQuantity * this.licenceUnitPriceMinor * 12
    },
    licenceTwoYearsMinor() {
      return this.licenceQuantity * this.licenceUnitPriceMinor * 24
    },
    licenceDisplayAmount() {
      return this.fromMinor(this.billingCycle === 'two_year' ? this.licenceTwoYearsMinor : this.licenceOneYearMinor)
    },
    allItems() {
      return [
        ...this.languageItems,
        ...this.standardItems,
        ...this.licenceItems
      ]
    },
    selectedDetails() {
      const selectedAddOns = this.selectedKeys
        .map(key => this.allItems.find(item => item.key === key))
        .filter(Boolean)
      return [
        ...selectedAddOns,
        ...this.licenceItems
      ]
    },
    totalAmountMinor() {
      return this.selectedDetails.reduce((total, item) => total + this.getItemPriceMinor(item), 0)
    },
    totalAmount() {
      return this.fromMinor(this.totalAmountMinor)
    },
    checkoutAmount() {
      const amount = this.createdOrder && this.createdOrder.finalAmount != null
        ? this.fromMinor(this.createdOrder.finalAmount)
        : this.totalAmount
      return amount
    },
    paymentOrderData() {
      return {
        serviceName: this.$t('controlCenter.sidebar.addOnManagement'),
        planId: 'add_on',
        billingCycle: this.billingCycle,
        orderNo: this.createdOrder && this.createdOrder.orderNo,
        currency: this.currency,
        addOnItems: this.selectedDetails
      }
    }
  },
  created() {
    this.restorePaymentReturn()
    this.loadOptions()
  },
  methods: {
    async loadOptions() {
      this.loading = true
      this.loadError = false
      try {
        try {
          this.addOnOptions = await paymentAdminApi.getAddOnOptions()
          this.purchaseConfig = (this.addOnOptions && this.addOnOptions.purchaseConfig) ||
            (this.addOnOptions && this.addOnOptions.paymentConfig) ||
            this.addOnOptions ||
            await paymentAdminApi.getPurchaseConfig()
        } catch (error) {
          this.addOnOptions = null
          const responses = await Promise.all([
            paymentAdminApi.getPurchaseConfig(),
            this.loadCatalogItemsSafely()
          ])
          this.purchaseConfig = responses[0]
          this.catalogItems = responses[1]
        }
        if (!this.catalogItems.length) {
          this.catalogItems = await this.loadCatalogItemsSafely()
        }
        await this.loadMyProductsSafely()
        this.syncSelection()
      } catch (error) {
        console.error('Failed to load add-on options:', error)
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    goToShop() {
      this.$router.push('/buy')
    },
    async loadCatalogItemsSafely() {
      try {
        const items = await paymentAdminApi.getCatalogItems()
        return Array.isArray(items) ? items : []
      } catch (error) {
        console.warn('Failed to load add-on catalog fallback:', error)
        return []
      }
    },
    async loadMyProductsSafely() {
      try {
        const response = await paymentAdminApi.getMyProducts()
        const data = response && response.data ? response.data : response
        this.myProducts = Array.isArray(data) ? data : (data && Array.isArray(data.products) ? data.products : [])
      } catch (error) {
        console.warn('Failed to load current package for add-on management:', error)
        this.myProducts = []
      }
    },
    normalizeBackendItems(keys, forcedType) {
      if (!this.addOnOptions) return []
      let source = []
      keys.some(key => {
        const value = this.addOnOptions[key]
        if (Array.isArray(value)) {
          source = value
          return true
        }
        if (value && Array.isArray(value.items)) {
          source = value.items
          return true
        }
        return false
      })
      return source.map(raw => {
        const itemType = String(forcedType || raw.itemType || raw.type || '').toUpperCase()
        const itemCode = raw.itemCode || raw.code || raw.id
        const catalog = itemType === 'AUDITOR_SEAT'
          ? this.findAuditorSeatCatalog(raw.quantity || raw.seats)
          : this.findCatalogItem(itemType, itemCode)
        return this.createItem({
          itemType,
          itemCode,
          name: raw.name || raw.label || raw.itemName || raw.itemCode || raw.code || raw.id,
          description: raw.description || raw.subtitle || this.backendItemDescription(raw, forcedType),
          quantity: raw.quantity || raw.seats || 1,
          priceOneYear: raw.priceOneYear || raw.oneYearPrice || raw.price || raw.unitPrice || (catalog && catalog.priceOneYear),
          priceTwoYears: raw.priceTwoYears || raw.twoYearPrice || (catalog && catalog.priceTwoYears),
          expiresAt: raw.expiresAt || raw.currentExpiresAt || raw.currentExpiry
        })
      })
    },
    backendItemDescription(raw, forcedType) {
      const type = forcedType || raw.itemType || raw.type
      if (type === 'LANGUAGE') return String(raw.code || raw.itemCode || '').toUpperCase()
      if (type === 'STANDARD') return this.$t('controlCenter.addOnManagement.standardTemplate')
      return this.$t('controlCenter.addOnManagement.licenceCapacity')
    },
    findCatalogItem(itemType, itemCode) {
      return this.catalogItems.find(item =>
        String(item.itemType || '').toUpperCase() === itemType &&
        String(item.itemCode || item.code || '') === String(itemCode || '')
      )
    },
    findAuditorSeatCatalog(seats) {
      return this.catalogItems.find(item =>
        String(item.itemType || '').toUpperCase() === 'AUDITOR_SEAT' &&
        Number(item.seatCount || item.seats || 0) === Number(seats)
      )
    },
    normalizeLicenceMonthlyUnitPrice(item) {
      const directMonthly = Number(item.pricePerSeat || item.monthlyPricePerSeat || item.pricePerMonth || 0)
      if (directMonthly > 0) return directMonthly
      const seats = Math.max(1, Number(item.seatCount || item.seats || 1))
      const yearlyTierPrice = Number(item.priceOneYear || item.oneYearPrice || 0)
      if (yearlyTierPrice > 0) return Math.round(yearlyTierPrice / seats / 12)
      return 0
    },
    createItem(raw) {
      const itemType = String(raw.itemType || '').toUpperCase()
      const itemCode = String(raw.itemCode || '')
      const quantity = Number(raw.quantity || 1)
      return {
        key: `${itemType}:${itemCode}:${quantity}`,
        itemType,
        itemCode,
        name: raw.name,
        description: raw.description,
        quantity,
        priceOneYear: Number(raw.priceOneYear || 0),
        priceTwoYears: raw.priceTwoYears == null ? null : Number(raw.priceTwoYears || 0),
        expiresAt: raw.expiresAt || null,
        typeLabel: this.typeLabel(itemType),
        quantityLabel: this.quantityLabel(itemType, quantity)
      }
    },
    typeLabel(itemType) {
      const key = {
        LANGUAGE: 'language',
        STANDARD: 'standard',
        AUDITOR_SEAT: 'auditorSeat',
        ADMIN_SEAT: 'adminSeat'
      }[itemType] || 'addOn'
      return this.$t(`controlCenter.addOnManagement.types.${key}`)
    },
    quantityLabel(itemType, quantity) {
      if (itemType === 'LANGUAGE' || itemType === 'STANDARD') {
        return this.$t('controlCenter.addOnManagement.singleCapability')
      }
      return this.$t('controlCenter.addOnManagement.seatQuantity', { count: quantity })
    },
    languageLabel(language) {
      const id = language.id || language.code
      const translated = this.$t(`buy.languages.items.${id}`)
      return translated === `buy.languages.items.${id}` ? language.name || id : translated
    },
    planLabel(planId) {
      const plan = this.purchaseConfig && this.purchaseConfig.plans && this.purchaseConfig.plans[planId]
      if (plan && plan.name) return plan.name
      const key = `controlCenter.auditDashboard.plans.${planId}`
      const translated = this.$t(key)
      return translated === key ? planId || '-' : translated
    },
    itemIcon(item) {
      const iconMap = {
        LANGUAGE: 'el-icon-chat-line-square',
        STANDARD: 'el-icon-document-checked',
        AUDITOR_SEAT: 'el-icon-user-solid',
        ADMIN_SEAT: 'el-icon-s-custom'
      }
      return iconMap[item.itemType] || 'el-icon-plus'
    },
    itemIconClass(item) {
      const classMap = {
        LANGUAGE: 'language-icon',
        STANDARD: 'standard-icon',
        AUDITOR_SEAT: 'auditor-icon',
        ADMIN_SEAT: 'admin-icon'
      }
      return classMap[item.itemType] || 'addon-icon'
    },
    decreaseLicence() {
      if (this.isAddOnBlocked) {
        this.showPackageRestriction()
        return
      }
      this.licenceQuantity = Math.max(0, this.licenceQuantity - 1)
    },
    increaseLicence() {
      if (this.isAddOnBlocked) {
        this.showPackageRestriction()
        return
      }
      this.licenceQuantity = Math.min(this.licenceMaxQuantity, this.licenceQuantity + 1)
    },
    toggleItem(item) {
      if (this.isAddOnBlocked) {
        this.showPackageRestriction()
        return
      }
      if (this.isSelected(item.key)) {
        this.selectedKeys = this.selectedKeys.filter(key => key !== item.key)
      } else {
        this.selectedKeys = [...this.selectedKeys, item.key]
      }
    },
    isSelected(key) {
      return this.selectedKeys.includes(key)
    },
    syncSelection() {
      if (this.isAddOnBlocked) {
        this.selectedKeys = []
        this.licenceQuantity = 0
        return
      }
      const available = new Set(this.allItems.map(item => item.key))
      this.selectedKeys = this.selectedKeys.filter(key => available.has(key))
    },
    getItemPriceMinor(item) {
      if (!item) return 0
      if (this.billingCycle === 'two_year') {
        return item.priceTwoYears != null ? Number(item.priceTwoYears) : Number(item.priceOneYear || 0) * 2
      }
      return Number(item.priceOneYear || 0)
    },
    getItemPrice(item) {
      return this.fromMinor(this.getItemPriceMinor(item))
    },
    fromMinor(amount) {
      return Math.round(Number(amount || 0)) / 100
    },
    formatPrice(amount) {
      return `${this.currency} ${formatPriceValue(Number(amount || 0))}`
    },
    formatDate(value) {
      if (!value) return this.$t('controlCenter.common.unknown')
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString()
    },
    addYears(value, years) {
      const base = value ? new Date(value) : new Date()
      const now = new Date()
      const start = Number.isNaN(base.getTime()) || base < now ? now : base
      const next = new Date(start)
      next.setFullYear(next.getFullYear() + years)
      return next
    },
    expiryLabel(item) {
      const current = item.expiresAt
        ? this.formatDate(item.expiresAt)
        : this.$t('controlCenter.addOnManagement.notActive')
      const projected = this.formatDate(this.addYears(item.expiresAt, this.durationYears))
      return this.$t('controlCenter.addOnManagement.expiryChange', { current, projected })
    },
    buildOrderPayload() {
      return {
        billingCycle: this.billingCycle,
        durationYears: this.durationYears,
        paymentMode: 'ONE_TIME',
        paymentCurrency: (this.paymentConfig && this.paymentConfig.paymentCurrency) || 'CNY',
        catalogCurrency: (this.paymentConfig && this.paymentConfig.paymentCurrency) || 'CNY',
        addOnItems: this.selectedDetails.map(item => ({
          itemType: item.itemType,
          itemCode: item.itemCode,
          quantity: item.quantity,
          unitPrice: this.getItemPriceMinor(item),
          durationYears: this.durationYears
        })),
        purchaseSnapshot: {
          billingCycle: this.billingCycle,
          addOnItems: this.selectedDetails,
          clientFinalAmount: this.totalAmount
        }
      }
    },
    async createOrder() {
      if (this.isAddOnBlocked) {
        this.showPackageRestriction()
        return
      }
      if (!this.selectedDetails.length) {
        this.$message.warning(this.$t('controlCenter.addOnManagement.selectRequired'))
        return
      }
      this.creatingOrder = true
      try {
        this.createdOrder = await paymentAdminApi.createAddOnOrder(this.buildOrderPayload())
        this.checkoutVisible = true
        this.scrollTop()
      } catch (error) {
        console.error('Failed to create add-on order:', error)
        this.$message.error(this.$t('controlCenter.addOnManagement.createOrderError'))
      } finally {
        this.creatingOrder = false
      }
    },
    restorePaymentReturn() {
      const query = (this.$route && this.$route.query) || {}
      const orderNo = Array.isArray(query.order_no) ? query.order_no[0] : query.order_no
      const hasPaymentReturn = query.add_on_checkout === '1' ||
        query.payment_intent_client_secret ||
        query.checkout_session_id ||
        query.alipay_return === '1' ||
        query.payment_method === 'alipay'
      if (!hasPaymentReturn || !orderNo) return
      this.createdOrder = { orderNo }
      this.checkoutVisible = true
    },
    returnToBuilder() {
      this.checkoutVisible = false
      this.createdOrder = null
    },
    handlePaymentSuccess() {
      this.loadOptions()
    },
    showPackageRestriction() {
      this.$message.warning(this.$t('controlCenter.addOnManagement.starterRestriction'))
    },
    dateValue(value) {
      if (!value) return Number.MAX_SAFE_INTEGER
      const date = new Date(value).getTime()
      return Number.isNaN(date) ? Number.MAX_SAFE_INTEGER : date
    },
    isExpired(product) {
      const value = this.dateValue(product.expiresAt)
      return value !== Number.MAX_SAFE_INTEGER && value < Date.now()
    },
    scrollTop() {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  },
  watch: {
    billingCycle() {
      this.syncSelection()
    }
  }
}
</script>

<style lang="scss" scoped>
.add-on-page {
  --addon-gradient: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
  --addon-accent: #0a7fd7;
  --addon-accent-dark: #0649b8;
  --addon-cyan: #03d2c5;
  --addon-bg: #f4f8ff;
  --addon-surface: #ffffff;
  --addon-border: #e2e8f0;
  --addon-muted: #65758b;
  padding-bottom: 24px;
}

.add-on-header {
  gap: 16px;

  .header-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
  }

  .header-actions ::v-deep .el-button + .el-button {
    margin-left: 0;
  }

  ::v-deep .el-button--default {
    border-color: rgba(10, 127, 215, 0.22);
    color: var(--addon-accent-dark);
    font-weight: 700;

    &:hover,
    &:focus {
      border-color: var(--addon-accent);
      color: var(--addon-accent);
      background: #f2f8ff;
    }
  }
}

.add-on-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 24px;
  align-items: start;
}

.package-status-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
  padding: 22px 24px;
  border: 1px solid rgba(10, 127, 215, 0.18);
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff, #eef8ff);
  box-shadow: 0 14px 34px rgba(6, 73, 184, 0.06);

  h2 {
    margin: 4px 0 6px;
    color: #172033;
    font-size: 22px;
  }

  p {
    margin: 0;
    color: var(--addon-muted);
    line-height: 1.5;
  }

  &.blocked {
    border-color: rgba(245, 158, 11, 0.28);
    background: linear-gradient(135deg, #fffaf0, #ffffff);
  }
}

.package-badge {
  flex: 0 0 auto;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: #e9fbfa;
  color: #047a6f;
  display: inline-flex;
  align-items: center;
  font-weight: 800;
  white-space: nowrap;

  &.starter {
    background: #fff7ed;
    color: #b45309;
  }
}

.catalog-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.duration-panel,
.category-section,
.summary-panel,
.checkout-panel {
  background: var(--addon-surface);
  border: 1px solid var(--addon-border);
  border-radius: 16px;
  box-shadow: 0 14px 34px rgba(6, 73, 184, 0.06);
}

.duration-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 22px 24px;
  background: linear-gradient(180deg, #ffffff, #f8fbff);

  h2 {
    margin: 4px 0 0;
    font-size: 20px;
    color: #1d2939;
  }
}

.section-eyebrow {
  display: block;
  color: var(--addon-accent-dark);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}

.duration-switch {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(96px, 1fr));
  padding: 4px;
  border: 1px solid #dbe7f5;
  border-radius: 14px;
  overflow: hidden;
  background: #eef5ff;

  button {
    min-height: 42px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #344054;
    font-weight: 700;
    cursor: pointer;
    padding: 0 18px;
    transition: all 0.25s ease;
  }

  button + button {
    border-left: 0;
  }

  .active {
    background: var(--addon-gradient);
    color: #fff;
    box-shadow: 0 8px 18px rgba(10, 127, 215, 0.24);
  }
}

.category-section {
  padding: 24px;
}

.category-heading {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;

  h2 {
    margin: 4px 0 0;
    font-size: 20px;
    color: #1d2939;
  }

  p {
    max-width: 380px;
    margin: 0;
    color: var(--addon-muted);
    font-size: 14px;
    line-height: 1.6;
  }
}

.item-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
}

.add-on-card {
  position: relative;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: start;
  gap: 14px;
  min-height: 182px;
  padding: 20px;
  border: 2px solid var(--addon-border);
  border-radius: 16px;
  background: linear-gradient(180deg, #fff, #fbfdff);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease, background 0.25s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(10, 127, 215, 0.42);
    box-shadow: 0 10px 24px rgba(6, 73, 184, 0.08);
    transform: translateY(-2px);
  }

  &.disabled,
  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
    transform: none;
    box-shadow: none;
  }

  &.selected {
    border-color: #0a7fd7;
    box-shadow: 0 0 0 3px rgba(3, 210, 197, 0.13), 0 14px 28px rgba(6, 73, 184, 0.12);
    background: linear-gradient(180deg, #f4fbff, #ffffff);

    &::before {
      opacity: 1;
    }

    .card-check {
      background: var(--addon-gradient);
      color: #fff;
      border-color: transparent;
    }

    .item-icon {
      background: var(--addon-gradient);
      color: #fff;
      box-shadow: 0 10px 22px rgba(10, 127, 215, 0.26);
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 4px;
    background: var(--addon-gradient);
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  strong {
    display: block;
    color: #172033;
    font-size: 17px;
    line-height: 1.35;
    margin: 6px 34px 8px 0;
  }
}

.card-check {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  border: 2px solid #c9d4df;
  border-radius: 999px;
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.item-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--addon-accent);
  background: #eef6ff;
  transition: all 0.25s ease;

  i {
    font-size: 22px;
  }
}

.auditor-icon,
.admin-icon {
  color: var(--addon-accent-dark);
  background: linear-gradient(180deg, #eef6ff, #e9fbfa);
}

.item-content {
  display: block;
  min-width: 0;
}

.item-type {
  color: var(--addon-accent-dark);
  font-size: 12px;
  font-weight: 700;
}

.item-meta {
  color: var(--addon-muted);
  font-size: 13px;
  line-height: 1.45;
  min-height: 38px;
}

.item-footer {
  grid-column: 1 / -1;
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  color: #172033;
  font-weight: 800;
  padding-top: 12px;
  border-top: 1px solid #edf2f8;

  span {
    font-size: 17px;
  }

  small {
    color: var(--addon-muted);
    font-weight: 600;
  }
}

.item-expiry {
  grid-column: 1 / -1;
  margin-top: 8px;
  color: #475467;
  font-size: 12px;
}

.category-licences {
  .licence-step-wrapper {
    display: block;
  }

  .licence-step-card {
    position: relative;
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) minmax(220px, auto) minmax(170px, auto);
    align-items: center;
    gap: 20px;
    padding: 28px;
    border: 2px solid var(--addon-border);
    border-radius: 16px;
    background: linear-gradient(180deg, #fff, #fbfdff);
    overflow: hidden;
    transition: all 0.25s ease;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 4px;
      background: var(--addon-gradient);
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    &.selected {
      border-color: #0a7fd7;
      box-shadow: 0 0 0 3px rgba(3, 210, 197, 0.13), 0 14px 28px rgba(6, 73, 184, 0.12);
      background: linear-gradient(180deg, #f4fbff, #ffffff);

      &::before {
        opacity: 1;
      }

      .card-check {
        background: var(--addon-gradient);
        color: #fff;
        border-color: transparent;
      }

      .item-icon {
        background: var(--addon-gradient);
        color: #fff;
        box-shadow: 0 10px 22px rgba(10, 127, 215, 0.26);
      }
    }

    strong {
      display: block;
      font-size: 18px;
      color: #172033;
      margin: 6px 0 8px;
    }

    .item-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
    }
  }

  .licence-main {
    min-width: 0;
  }

  .licence-unit-price {
    display: inline-flex;
    margin-top: 12px;
    padding: 6px 10px;
    border-radius: 999px;
    background: #eef6ff;
    color: var(--addon-accent-dark);
    font-size: 12px;
    font-weight: 700;
  }

  .licence-control {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
  }

  .licence-counter-btn {
    width: 36px;
    height: 36px;
    border: 2px solid #d9e5f2;
    border-radius: 50%;
    background: #fff;
    color: var(--addon-accent-dark);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      border-color: var(--addon-accent);
      color: var(--addon-accent);
      box-shadow: 0 8px 18px rgba(10, 127, 215, 0.14);
    }

    &:disabled {
      cursor: not-allowed;
      color: #b7c2d0;
      background: #f4f7fb;
    }
  }

  .licence-count {
    min-width: 90px;
    text-align: center;

    span {
      display: block;
      color: var(--addon-accent-dark);
      font-size: 48px;
      font-weight: 800;
      line-height: 1;
    }

    small {
      display: block;
      margin-top: 4px;
      color: #94a3b8;
      font-size: 13px;
      font-weight: 700;
    }
  }

  .licence-total {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;

    span {
      color: var(--addon-accent-dark);
      font-size: 22px;
      font-weight: 800;
      white-space: nowrap;
    }

    small {
      margin-top: 4px;
      color: var(--addon-muted);
      font-size: 12px;
      font-weight: 700;
    }
  }
}

.empty-category {
  padding: 28px;
  border: 1px dashed var(--addon-border);
  border-radius: 8px;
  color: var(--addon-muted);
  text-align: center;
}

.summary-column {
  position: sticky;
  top: 22px;
}

.summary-panel {
  padding: 22px;
  background: linear-gradient(180deg, #fff, #f8fbff);
}

.summary-header {
  h2 {
    margin: 4px 0 0;
    color: #1d2939;
    font-size: 20px;
  }
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 20px 0;
}

.summary-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf1f5;

  div,
  span,
  small {
    min-width: 0;
  }

  strong,
  span,
  small {
    display: block;
  }

  strong {
    color: #172033;
    font-size: 14px;
  }

  span,
  small {
    margin-top: 3px;
    color: var(--addon-muted);
    font-size: 12px;
    line-height: 1.4;
  }

  em {
    color: #172033;
    font-size: 14px;
    font-style: normal;
    font-weight: 800;
    white-space: nowrap;
  }
}

.summary-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 22px 0;
  padding: 26px 16px;
  border-radius: 8px;
  background: #f1f7ff;
  color: var(--addon-muted);
  text-align: center;

  i {
    color: var(--addon-accent);
    font-size: 26px;
  }

  p {
    margin: 0;
  }
}

.total-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 20px 0 16px;
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eef6ff, #e9fbfa);

  span {
    color: #475467;
    font-weight: 700;
  }

  strong {
    color: var(--addon-accent-dark);
    font-size: 24px;
  }
}

.confirm-button {
  width: 100%;

  ::v-deep &.el-button--primary {
    border: 0;
    background: var(--addon-gradient);
    box-shadow: 0 10px 24px rgba(10, 127, 215, 0.24);

    &:hover,
    &:focus {
      filter: brightness(1.03);
    }

    &.is-disabled {
      opacity: 0.55;
      filter: none;
    }
  }
}

.summary-note {
  margin: 12px 0 0;
  color: var(--addon-muted);
  font-size: 12px;
  line-height: 1.5;
}

.checkout-panel {
  padding: 28px;
}

.checkout-heading {
  max-width: 720px;
  margin: 0 auto 20px;
  text-align: center;

  h2 {
    margin: 6px 0 8px;
    color: #172033;
    font-size: 24px;
  }

  p {
    margin: 0;
    color: var(--addon-muted);
  }
}

.checkout-kicker {
  color: var(--addon-accent);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
}

.add-on-state {
  min-height: 260px;
}

.mobile-confirm-bar {
  display: none;
}

@media (max-width: 1100px) {
  .add-on-workspace {
    grid-template-columns: 1fr;
  }

  .category-licences {
    .licence-step-card {
      grid-template-columns: 56px minmax(0, 1fr) minmax(220px, auto);
    }

    .licence-total {
      grid-column: 2 / -1;
      align-items: flex-start;
    }
  }

  .package-status-panel {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-column {
    position: static;
  }
}

@media (max-width: 768px) {
  .add-on-page {
    padding-bottom: 96px;
  }

  .package-status-panel,
  .duration-panel,
  .category-section,
  .summary-panel,
  .checkout-panel {
    border-radius: 12px;
  }

  .add-on-header,
  .duration-panel,
  .category-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .duration-switch {
    width: 100%;
  }

  .item-grid {
    grid-template-columns: 1fr;
  }

  .add-on-card {
    min-height: auto;
  }

  .summary-column {
    display: none;
  }

  .checkout-panel {
    padding: 18px 12px;
  }

  .mobile-confirm-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--addon-border);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -10px 28px rgba(15, 23, 42, 0.12);

    span,
    strong {
      display: block;
    }

    span {
      color: var(--addon-muted);
      font-size: 12px;
      font-weight: 700;
    }

    strong {
      color: var(--addon-accent-dark);
      font-size: 18px;
      overflow-wrap: anywhere;
    }

    ::v-deep .el-button--primary {
      flex: 0 0 auto;
      border: 0;
      background: var(--addon-gradient);
      box-shadow: 0 8px 18px rgba(10, 127, 215, 0.24);
    }
  }
}

@media (max-width: 640px) {
  .category-licences {
    .licence-step-card {
      grid-template-columns: 48px minmax(0, 1fr);
      padding: 22px;

      .item-icon {
        width: 48px;
        height: 48px;
      }

      .licence-control,
      .licence-total {
        grid-column: 1 / -1;
        width: 100%;
      }

      .licence-control {
        gap: 14px;
        justify-content: space-between;
        padding-top: 16px;
        border-top: 1px solid #edf2f8;
      }

      .licence-total {
        align-items: flex-start;
      }

      .licence-count {
        min-width: 74px;

        span {
          font-size: 38px;
        }
      }
    }
  }
}

@media (max-width: 420px) {
  .category-section,
  .duration-panel,
  .checkout-panel {
    padding: 16px;
  }

  .add-on-card {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 12px;
    padding: 16px;

    strong {
      margin-right: 28px;
      overflow-wrap: anywhere;
    }
  }

  .item-icon {
    width: 40px;
    height: 40px;
  }

  .item-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .mobile-confirm-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .mobile-confirm-bar ::v-deep .el-button {
    width: 100%;
  }
}
</style>
